import React, { useEffect, useState, useRef, useCallback } from "react"
import MetaTags from "react-meta-tags"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"
import { del, get, post } from "helpers/api_helper"
import { userModuleData, userRole } from "helpers/basic_helper"
import { EMPLOYEE_URL } from "helpers/url_helper"
import { useDispatch, useSelector } from "react-redux"
import {
  addEmployeeRecord,
  getEmployeeAllRows,
  getEmployeeAllRowsSuccess,
  removeEmployeeRecord,
} from "store/actions"
import Form from "./component/Form"
import Datatables from "components/Datatables/datatable"
import { Link } from "react-router-dom"
import Notifications from "components/Notifications"
import { runBatchLoader } from "utils/batchLoader"

const Employee = props => {
  const dispatch = useDispatch()
  const { loading, employees } = useSelector(state => state.Employee)
  const [open, isOpen] = useState(false)
  const [editableObj, setEditableObj] = useState(null)
  const [dataRows, setDataRows] = useState([])
  const [DDMasters, setDDMasters] = useState([])
  const [isDelete, setIsDelete] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isActions, setIsActions] = useState(false)
  const [isActionColumn, setIsActionColumn] = useState(false)
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })
  const [batchProgress, setBatchProgress] = useState(null)
  const batchCancelRef = useRef(false)
  const isBatchRunningRef = useRef(false)
  const totalRowsRef = useRef(0)

  useEffect(() => {
    const count = employees?.total_rows || dataRows?.total_rows
    if (count) {
      totalRowsRef.current = count
    }
  }, [employees, dataRows])

  const handleCancelBatch = () => {
    batchCancelRef.current = true
    isBatchRunningRef.current = false
    setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null)
    setIsLoading(false)
  }

  const handlePaginationChange = useCallback((newPag) => {
    setPagination(prev => {
      if (
        prev.page === newPag.page &&
        prev.limit === newPag.limit &&
        prev.search === newPag.search &&
        Boolean(prev.isAll) === Boolean(newPag.isAll)
      ) {
        return prev
      }
      return { ...prev, ...newPag }
    })
  }, [])

  useEffect(() => {
    const handler = setTimeout(() => {
      getData()
    }, 300)

    return () => clearTimeout(handler)
  }, [pagination])

  const getData = async () => {
    const isAllSelected = pagination?.isAll || pagination?.limit === 'all' || (typeof pagination?.limit === 'number' && pagination?.limit > 100)

    const role = await userRole()
    if (role == "Superadmin") {
      const data = {
        edit: true,
        delete: true,
        add: true,
      }

      setIsActions(data)
      if (data && (data?.edit || data?.delete)) {
        setIsActionColumn(true)
      }
    } else {
      const data = await userModuleData("employee_master", "employees")
      setIsActions(data)
      if (data && (data?.edit || data?.delete)) {
        setIsActionColumn(true)
      }
    }

    if (!DDMasters || !DDMasters.branches || (Array.isArray(DDMasters) && DDMasters.length === 0)) {
      const opts = await get("/api/employee/getOptions")
      if (opts && (opts.status === 200 || opts.response)) {
        setDDMasters(opts.response || opts)
      }
    }

    if (isAllSelected) {
      batchCancelRef.current = true
      await new Promise(r => setTimeout(r, 60))
      batchCancelRef.current = false
      isBatchRunningRef.current = true

      setIsLoading(true)
      try {
        await runBatchLoader({
          fetchPage: async (page, batchSize) => {
            const res = await get(`${EMPLOYEE_URL}?page=${page}&limit=${batchSize}&search=${pagination?.search || ''}`)
            return res?.response || res || {}
          },
          batchSize: 250,
          initialTotal: totalRowsRef.current || 0,
          onProgress: setBatchProgress,
          onRowsLoaded: (accumulatedRows, totalCount) => {
            const payload = {
              total_rows: totalCount,
              rows: accumulatedRows
            }
            setDataRows(payload)
            dispatch(getEmployeeAllRowsSuccess({ isEdit: false, data: payload }))
          },
          cancelRef: batchCancelRef,
          runningRef: isBatchRunningRef,
        })
      } catch (err) {
        console.error("Error batch loading employees:", err)
      } finally {
        setIsLoading(false)
      }
    } else {
      setDataRows([])
      setIsLoading(true)
      setBatchProgress(null)
      const query = `${EMPLOYEE_URL}?page=${pagination?.page}&limit=${pagination?.limit}&search=${pagination?.search || ""}`
      dispatch(
        getEmployeeAllRows(
          {
            url: query,
            isSingle: false,
          },
          props.history
        )
      )
    }
  }

  useEffect(() => {
    setDataRows(employees)
    if (employees !== undefined && employees !== null) {
      setIsLoading(false)
    }
  }, [employees])

  useEffect(() => {
    if (!loading) {
      setIsLoading(false)
    }
  }, [loading])

  const getTableRows = data => {
    if (!data) return []
    if (Array.isArray(data)) return data
    if (Array.isArray(data.rows)) return data.rows
    if (Array.isArray(data.data)) return data.data
    return []
  }

  const getTableRowsCount = data => {
    if (!data) return 0
    if (typeof data.total_rows === "number") return data.total_rows
    if (typeof data.total === "number") return data.total
    if (Array.isArray(data)) return data.length
    if (Array.isArray(data.rows)) return data.rows.length
    if (Array.isArray(data.data)) return data.data.length
    return 0
  }

  /**
   * Add or Update Data
   * @param {Row_id} data
   */
  const submitForm = async data => {
    setIsLoading(true)

    const formatDateForSubmit = d => {
      if (!d) return ""
      if (typeof d === "string") return d
      if (d instanceof Date && !isNaN(d.getTime())) {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, "0")
        const day = String(d.getDate()).padStart(2, "0")
        return `${y}-${m}-${day}`
      }
      return ""
    }

    const Obj = {
      id: data?.id || "",
      firstname: data?.firstname || "",
      middlename: data?.middlename || "",
      lastname: data?.lastname || "",
      gender: data?.gender || "",
      code: data?.code || "",
      mobile: data?.mobile || "",
      phone: data?.phone || "",
      dob: formatDateForSubmit(data?.dob),
      doj: formatDateForSubmit(data?.doj),
      email: data?.email || "",
      role: data?.role || "",
      shift: data?.shift || "",
      shiftgroup: data?.shiftgroup || "",
      designation: data?.designation || "",
      department: data?.department || "",
      lab_department: data?.lab_department || "",
      location: data?.location || "",
      pan: data?.pan || "",
      uid: data?.uid || "",
      address1: data?.address1 || "",
      address2: data?.address2 || "",
      branch: data?.branch || data?.branch_id || "",
    }

    try {
      const url = `${EMPLOYEE_URL}${data?.id ? `?q=${data.id}` : ""}`
      const res = await post(url, Obj)

      if (res && (res.status === 200 || res.status === 201 || res.response)) {
        Notifications(data?.id ? "Employee updated successfully!" : "Employee added successfully!", "success")
        isOpen(false)
        setIsEdit(false)
        setEditableObj(null)
        getData()
      } else {
        Notifications(res?.message || "Failed to save employee record.", "error")
      }
    } catch (err) {
      console.error("Employee save error:", err)
      Notifications(err?.message || "An error occurred while saving employee record.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Get Single row using url params ?q=?
   * @param {Row_id} q
   * @method {get} GET
   */
  const handleEdit = async q => {
    try {
      setIsLoading(true)
      const opts = await get(`${EMPLOYEE_URL}?q=${q}`)
      let empData = null
      if (opts?.response) {
        empData = Array.isArray(opts.response) ? opts.response[0] : opts.response
      } else if (opts?.data) {
        empData = Array.isArray(opts.data) ? opts.data[0] : opts.data
      } else if (opts?.id) {
        empData = opts
      }

      if (empData) {
        setEditableObj(empData)
        setIsEdit(q)
        isOpen(true)
      } else {
        Notifications("Failed to fetch employee details.", "error")
      }
    } catch (err) {
      console.error("handleEdit error:", err)
      Notifications("Error fetching employee details.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Chnage Row Status By ID and condition Based Status passing,
   * @param {row_id} q
   * @param {row Status} status
   * @method {del} DELETE
   */
  const handleRemove = async (data, status) => {
    if (data > 0) {
      setIsDelete(data)
      try {
        const res = await del(`${EMPLOYEE_URL}?q=${data}&status=${status}`)
        if (res && res.status === 200) {
          Notifications(status == 1 ? "Employee deactivated successfully!" : "Employee activated successfully!", "success")
          getData()
        } else {
          Notifications(res?.message || "Failed to update employee status.", "error")
        }
      } catch (e) {
        Notifications("Error updating employee status.", "error")
      } finally {
        setIsDelete(false)
      }
    }
  }

  /**
   * DataTable with Condition based row formating
   */
  let datatableData = [
    {
      dataField: "id",
      text: "Id",
    },
    {
      dataField: "firstname",
      text: "Employee Name",
      formatter: (cell, row) =>
        [row?.firstname, row?.middlename, row?.lastname].filter(Boolean).join(" ") || "-",
    },
    {
      dataField: "code",
      text: "Employee Code",
      formatter: cell => cell || "-",
    },
    {
      dataField: "branch_name",
      text: "Branch",
      formatter: (cell, row) => row?.branch_name || row?.branch || "-",
    },
    {
      dataField: "email",
      text: "Email",
      formatter: cell => cell || "-",
    },
    {
      dataField: "mobile",
      text: "Mobile",
      formatter: cell => cell || "-",
    },
    {
      dataField: "gender",
      text: "Gender",
      formatter: (cell, row) =>
        row?.gender == 1 ? "Male" : row?.gender == 2 ? "Female" : (row?.gender || "-"),
    },
    {
      dataField: "designation",
      text: "Designation",
      formatter: cell => cell || "-",
    },
    {
      dataField: "action",
      text: "Action",
      isDummyField: true,
      formatter: (cell, row) => (
        <>
          <Link
            to="#"
            className="btn btn-outline-secondary btn-sm edit"
            onClick={() => handleEdit(row.id)}
            title="Edit"
          >
            <i className={`fas fa-pencil-alt`} />
          </Link>
          <Link
            to="#"
            className="ms-1 btn btn-outline-danger btn-sm edit"
            onClick={() => handleRemove(row.id, row.status, row.code)}
            title="Delete"
          >
            <i className={`fas fa-trash-alt`} />
          </Link>
        </>
      ),
    },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Dashboard | Employees</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Masters" breadcrumbItem={"Employee"} />
          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <Datatables
                    isSearch={true}
                    columns={datatableData}
                    showTableOnly={true}
                    rowsLength={getTableRowsCount(dataRows)}
                    rows={getTableRows(dataRows)}
                    keyField={"id"}
                    handleAddButton={() => {
                      setEditableObj(null)
                      isOpen(true)
                    }}
                    title="All Employees"
                    loading={(loading || isLoading) && (!batchProgress || batchProgress.loaded === 0)}
                    batchProgress={batchProgress}
                    onCancelBatch={handleCancelBatch}
                    isAdd={true}
                    isTableHead={true}
                    ssr={handlePaginationChange}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Datatable */}
      {open && (
        <Form
          key={editableObj?.id ? `edit_${editableObj.id}` : "add_employee"}
          title={`${editableObj ? "Edit" : "Add"} Employee`}
          Position={"right"}
          isOpen={open}
          width={60}
          DDMasters={DDMasters}
          length={getTableRowsCount(dataRows)}
          editableObj={editableObj}
          onDrawerClose={() => {
            setEditableObj(null)
            isOpen(false)
          }}
          submitForm={submitForm}
        />
      )}
      {/* End Datatable */}
    </React.Fragment>
  )
}

export default Employee
